/**
 * Entity Record List Component
 *
 * Displays a list of entity records using the DataTable from design system.
 */

import AddIcon from '@mui/icons-material/Add';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import DataIcon from '@mui/icons-material/DataObject';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import ExpandIcon from '@mui/icons-material/OpenInFull';
import {
  Box,
  DataTable,
  type DataTableColumn,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  PanelAttributeList,
  PanelSection,
  ShimmerLoader,
  SplitPanel,
  SplitPanelContent,
  SplitPanelFooter,
  SplitPanelHeader,
  Typography,
  useI18n,
  useSplitPanel,
} from '@ops-matrix/design-system-ui';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { type EntityRecord, useConsumerEntity, useConsumerRecords } from '../../hooks/useEntities';

interface EntityRecordListProps {
  entityId: string;
  title?: string;
  showCreateButton?: boolean;
  onRecordClick?: (record: EntityRecord) => void;
}

function formatFieldValue(value: unknown, fieldType?: string): string {
  if (value === null || value === undefined) return '-';

  switch (fieldType) {
    case 'CURRENCY':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }).format(Number(value));
    case 'DATE':
      return new Date(String(value)).toLocaleDateString();
    case 'DATETIME':
      return new Date(String(value)).toLocaleString();
    case 'BOOLEAN':
      return value ? 'Yes' : 'No';
    default:
      return String(value);
  }
}

function getStatusColor(status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' {
  const colorMap: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    new: 'info',
    draft: 'default',
    submitted: 'primary',
    processing: 'primary',
    underwriting: 'warning',
    approved: 'success',
    denied: 'error',
    closed: 'default',
  };
  return colorMap[status.toLowerCase()] ?? 'default';
}

export default function EntityRecordList({
  entityId,
  title,
  showCreateButton = true,
  onRecordClick,
}: EntityRecordListProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // URL sync for detail panel
  const urlSync = useMemo(
    () => ({
      getPanelId: () => searchParams.get('panelId'),
      setPanelId: (id: string | null) => {
        const newParams = new URLSearchParams(searchParams);
        if (id) {
          newParams.set('panelId', id);
        } else {
          newParams.delete('panelId');
        }
        setSearchParams(newParams, { replace: true });
      },
    }),
    [searchParams, setSearchParams],
  );

  const { data: entity, isLoading: entityLoading, error: entityError } = useConsumerEntity(entityId);
  const {
    data: recordsData,
    isLoading: recordsLoading,
    error: recordsError,
  } = useConsumerRecords(entityId, { page, limit: pageSize });

  const records = recordsData?.records ?? [];

  // Find record by ID for URL sync restore
  const findRecordById = useCallback((id: string) => records.find((r: EntityRecord) => r.id === id), [records]);

  // Detail panel state management with URL sync
  const { selectedItem, openPanel, closePanel, panelProps } = useSplitPanel<EntityRecord>({
    idField: 'id',
    urlSync,
    findItemById: findRecordById,
  });

  // Build columns from entity field definitions
  const columns = useMemo<DataTableColumn<EntityRecord>[]>(() => {
    if (!entity?.fields) return [];

    // Get first 4 fields that should show in list
    const listFields = entity.fields.slice(0, 4);

    const fieldColumns: DataTableColumn<EntityRecord>[] = listFields.map((field) => ({
      id: field.name,
      header: field.label,
      accessor: (row: EntityRecord) => row.data[field.name],
      sortable: true,
      cell: (value: unknown) => formatFieldValue(value, field.fieldType),
    }));

    // Add status column if records have status
    fieldColumns.push({
      id: 'status',
      header: 'Status',
      accessor: (row: EntityRecord) => row.status ?? row.data.status,
      sortable: true,
      cell: (_value: unknown, row: EntityRecord) => {
        const status = row.status ?? (row.data.status as string);
        if (!status) return '-';
        return (
          <GlassChip label={status} size="small" color={getStatusColor(status)} sx={{ textTransform: 'capitalize' }} />
        );
      },
    });

    // Add updated date
    fieldColumns.push({
      id: 'updatedAt',
      header: 'Updated',
      accessor: 'updatedAt',
      sortable: true,
      cell: (value: unknown) => new Date(value as string).toLocaleDateString(),
    });

    return fieldColumns;
  }, [entity?.fields]);

  // Handle row click - use callback if provided, otherwise open panel
  const handleRowClick = useCallback(
    (record: EntityRecord) => {
      if (onRecordClick) {
        onRecordClick(record);
      } else {
        openPanel(record);
      }
    },
    [onRecordClick, openPanel],
  );

  // Navigate to full detail page
  const handleViewFullDetails = useCallback(() => {
    if (selectedItem) {
      navigate(`/records/${entityId}/${selectedItem.id}`);
      closePanel();
    }
  }, [selectedItem, entityId, navigate, closePanel]);

  const handleCreate = () => {
    navigate(`/submit/${entityId}`);
  };

  // Build dynamic attributes from record data
  const recordAttributes = useMemo(() => {
    if (!selectedItem || !entity?.fields) return [];

    return entity.fields.map((field) => ({
      label: field.label,
      value: formatFieldValue(selectedItem.data[field.name], field.fieldType),
    }));
  }, [selectedItem, entity?.fields]);

  const isLoading = entityLoading || recordsLoading;
  const error = entityError || recordsError;

  // Panel content for split view
  const panelContent = selectedItem ? (
    <>
      <SplitPanelHeader
        title={entity?.name || t('common.record')}
        subtitle={selectedItem?.id ? `#${selectedItem.id.slice(0, 8)}` : undefined}
        onClose={closePanel}
        actions={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <GlassTooltip title={t('common.edit')}>
              <GlassIconButton
                size="small"
                onClick={() => {
                  if (selectedItem) {
                    navigate(`/records/${entityId}/${selectedItem.id}/edit`);
                    closePanel();
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </GlassIconButton>
            </GlassTooltip>
          </Box>
        }
      />
      <SplitPanelContent>
        {/* Status Section */}
        <PanelSection title={t('common.status')} icon={<InfoIcon />} collapsible={false}>
          <Box sx={{ mb: 2 }}>
            {(selectedItem.status || (selectedItem.data.status as string)) && (
              <GlassChip
                label={selectedItem.status ?? (selectedItem.data.status as string)}
                size="small"
                color={getStatusColor(selectedItem.status ?? (selectedItem.data.status as string) ?? '')}
                sx={{ textTransform: 'capitalize' }}
              />
            )}
          </Box>
          <PanelAttributeList
            attributes={[
              {
                label: t('consumer.entity.recordId'),
                value: selectedItem.id.slice(0, 8),
                copyable: true,
              },
            ]}
          />
        </PanelSection>

        {/* Dynamic Fields Section */}
        {recordAttributes.length > 0 && (
          <PanelSection title={t('consumer.entity.fields')} icon={<DataIcon />} defaultExpanded>
            <PanelAttributeList attributes={recordAttributes} />
          </PanelSection>
        )}

        {/* Timeline Section */}
        <PanelSection title={t('consumer.proposals.timeline')} icon={<CalendarIcon />} defaultExpanded>
          <PanelAttributeList
            attributes={[
              {
                label: 'Created',
                value: selectedItem.createdAt ? new Date(selectedItem.createdAt).toLocaleDateString() : 'N/A',
                icon: <CalendarIcon fontSize="small" />,
              },
              {
                label: 'Updated',
                value: selectedItem.updatedAt ? new Date(selectedItem.updatedAt).toLocaleDateString() : 'N/A',
              },
            ]}
          />
        </PanelSection>
      </SplitPanelContent>
      <SplitPanelFooter align="space-between">
        <GlassButton variant="outlined" startIcon={<ExpandIcon />} onClick={handleViewFullDetails}>
          {t('common.viewDetails')}
        </GlassButton>
        <GlassButton
          variant="gradient"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => {
            navigate(`/records/${entityId}/${selectedItem.id}/edit`);
            closePanel();
          }}
        >
          {t('common.edit')}
        </GlassButton>
      </SplitPanelFooter>
    </>
  ) : null;

  if (error) {
    return (
      <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
        Failed to load records. Please try again later.
      </GlassAlert>
    );
  }

  return (
    <SplitPanel
      panelOpen={panelProps.open}
      onPanelClose={closePanel}
      storageKey="consumer-entities-panel"
      panel={panelContent}
      panelAriaLabel={t('consumer.entity.entityDetails')}
    >
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {title ?? entity?.pluralName ?? 'Records'}
            </Typography>
            {entity?.description && (
              <Typography variant="body2" color="textSecondary">
                {entity.description}
              </Typography>
            )}
          </Box>
          {showCreateButton && entity?.allowCreate && (
            <GlassButton variant="outlined" startIcon={<AddIcon />} onClick={handleCreate}>
              {t('consumer.entity.newEntity', { entity: entity?.name })}
            </GlassButton>
          )}
        </Box>

        {/* Data Table */}
        <GlassCard animate>
          {isLoading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <ShimmerLoader variant="rectangular" height={48} />
                </Box>
              ))}
            </Box>
          ) : records.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="textSecondary" sx={{ mb: 2 }}>
                {t('consumer.entity.noRecords', { entities: entity?.pluralName?.toLowerCase() ?? 'records' })}
              </Typography>
              {entity?.allowCreate && (
                <GlassButton variant="outlined" startIcon={<AddIcon />} onClick={handleCreate}>
                  {t('consumer.entity.createFirst', { entity: entity?.name?.toLowerCase() })}
                </GlassButton>
              )}
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={records}
              getRowId={(row) => row.id}
              onRowClick={handleRowClick}
              pagination={{
                page: page - 1,
                pageSize,
                totalRows: recordsData?.total ?? 0,
              }}
              onPaginationChange={(config) => {
                setPage(config.page + 1);
                setPageSize(config.pageSize);
              }}
            />
          )}
        </GlassCard>
      </Box>
    </SplitPanel>
  );
}
